/**
 * Transport: REST calls and the socket lifecycle.
 *
 * Moved from components/text-widget/utils/service.ts. The old path
 * re-exports this, so the widget and its specs run against this code
 * unchanged.
 */

// Re-exported so the widget's existing imports from this module keep working.
// The core imports ./socket dynamically instead, which is the whole point.
export {
  handleSocketError,
  attemptConnectionRecovery,
  cleanupSocketConnection,
  connectSocket,
} from './socket';

import {
  FeedbackResponse,
  LiveChatMessageResponse,
  LiveChatSearchServerMessagesResponse,
  ButtonResponsePayload,
} from './wire';
import {
  getLiveChatAuthInfo,
  getOutgoingMessageHeaders,
  getOutgoingMessagesBody,
  removeLiveChatAuthInfo,
} from '../security/session';

/**
 * The slice of the widget's ServerConfig that transport actually reads.
 *
 * The widget's own ServerConfig has some sixty fields and carries Ionic event
 * handlers, which have no business in a headless core. These six are every
 * field this module touches; ServerConfig satisfies the shape structurally,
 * so existing callers pass it unchanged.
 */
export interface TransportConfig {
  locationId: string;
  marketplaceURL: string;
  widgetId?: string;
  defaultWidget?: boolean;
  developerVersion?: string;
  enableContactForm?: boolean;
}

type ServerConfig = TransportConfig;
const DEFAULT_DUMMY_REQUEST = false;

export class TooManyRequestsError extends Error {
  status = 429;
  responseBody?: string;
  constructor(responseBody?: string) {
    super('TooManyRequests');
    this.name = 'TooManyRequestsError';
    this.responseBody = responseBody;
  }
}

export const getPreviousChatMessages = async function ({
  serverConfig,
  conversationId,
  lastMessageId = '',
  pageLimit = 10,
  isDummy = DEFAULT_DUMMY_REQUEST,
}: {
  serverConfig: ServerConfig;
  conversationId: string;
  lastMessageId?: string;
  pageLimit?: number;
  isDummy?: boolean;
}): Promise<LiveChatSearchServerMessagesResponse> {
  if (isDummy) {
    return new Promise(resolve => {
      setTimeout(() => {
        let response = {
          messages: [
            {
              id: 'dummy-1',
              body: 'How are you',
              direction: 'incoming',
              dateAdded: '2023-09-26T14:36:49.566Z',
              messageType: 29,
            },
            {
              id: 'dummy-2',
              body: 'I am good',
              direction: 'outgoing',
              dateAdded: '2023-09-26T14:36:49.566Z',
              messageType: 29,
            },
          ],
          lastMessageId: '24',
        };

        resolve(response as LiveChatSearchServerMessagesResponse);
      }, 2000);
    });
  }
  let response = await fetch(
    serverConfig.marketplaceURL +
      `/conversations/providers/live-chat/messages/search?conversationId=${conversationId}&locationId=${serverConfig.locationId}&lastMessageId=${lastMessageId}&pageLimit=${pageLimit}`,
    {
      method: 'GET',
      // body: JSON.stringify({ locationId: serverConfig.locationId, page: 0 }),
      headers: getOutgoingMessageHeaders({
        locationId: serverConfig.locationId,
        widgetId: serverConfig.widgetId,
        defaultWidget: serverConfig.defaultWidget,
        developerVersion: serverConfig.developerVersion,
      }),
    },
  );

  if (response.ok) {
    let data: LiveChatSearchServerMessagesResponse = await response.json();
    return data;
  } else if (response.status === 429) {
    throw new TooManyRequestsError();
  }
};

export const postMessage = async function ({
  serverConfig,
  messageText,
  attachments,
  captchaToken,
  isDummy = DEFAULT_DUMMY_REQUEST,
  subType,
  buttonResponse,
}: {
  serverConfig: ServerConfig;
  messageText: string;
  attachments?: string[];
  captchaToken?: string;
  isDummy?: boolean;
  subType?: string;
  buttonResponse?: ButtonResponsePayload;
}): Promise<LiveChatMessageResponse> {
  if (isDummy) {
    return new Promise(resolve => {
      setTimeout(() => {
        let response = {
          locationId: 'hgHI41V5EbRCGvQ6p4TD',
          visitorId: '6c824709-c715-4236-9c39-630d37c899aa',
          conversationId: 'wY7PF1PGLszMIw7LifDq',
          contactId: 'pZ8aB2cD3eF4gH5iJ6kL',
          token: '123',
          messageDetails: {
            id: 'PQp1TkzluJ8dmb82gLEk',
            dateAdded: '2023-09-26T17:23:39.981Z',
          },
          traceId: 'da4c5312-3139-970b-8201-8e5dc0b4918e',
        };

        resolve(response);
      }, 2000);
    });
  }

  const sendRequest = async (): Promise<LiveChatMessageResponse> => {
    const body = getOutgoingMessagesBody({
      locationId: serverConfig.locationId,
      message: messageText,
      attachments,
      captchaToken,
      widgetId: serverConfig.widgetId,
      defaultWidget: serverConfig.defaultWidget,
      subType,
      buttonResponse,
    });

    return fetch(serverConfig.marketplaceURL + '/conversations/providers/live-chat/messages', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: getOutgoingMessageHeaders({
        locationId: serverConfig.locationId,
        widgetId: serverConfig.widgetId,
        defaultWidget: serverConfig.defaultWidget,
        developerVersion: serverConfig.developerVersion,
      }),
    })
      .then(response => response.text().then(responseBody => ({ response, responseBody })))
      .then(({ response, responseBody }) => {
        if (response.ok) {
          return JSON.parse(responseBody);
        } else if (response.status === 429) {
          throw new TooManyRequestsError(responseBody);
        } else if (response.status === 400) {
          const errorDetails = JSON.parse(responseBody);
          if (errorDetails.message.includes('clear conversation id and token')) {
            throw new Error('ConversationIsDeleted');
          }
          throw new Error('Bad Request: ' + errorDetails.message);
        } else {
          // The status and the server's words, so a failed send in a host's
          // console says what happened rather than only that it did.
          throw new Error(`Request failed: HTTP ${response.status} ${responseBody.slice(0, 200)}`);
        }
      });
  };

  return sendRequest().catch(error => {
    if (error.message === 'ConversationIsDeleted') {
      // Clear conversation ID and token from browser storage
      removeLiveChatAuthInfo(serverConfig.locationId, serverConfig.widgetId);
      if (!serverConfig.enableContactForm) {
        return sendRequest().then(conversationDeletedNewData => {
          conversationDeletedNewData.errorMessage = true;
          return conversationDeletedNewData;
        });
      } else {
        throw error;
      }
    } else {
      throw error;
    }
  });
};

/**
 * Interactive-card telemetry: delivered, clicked, timed out, link followed.
 * Ported from the widget's service.ts (PR #342) when that file became a
 * re-export of this one. Fire-and-forget — the card is already shown — but it
 * logs, so a 4xx or a rate limit stays traceable.
 */
export const postInteractiveEvent = function ({
  serverConfig,
  event,
  source,
}: {
  serverConfig: ServerConfig;
  event: 'card_delivered' | 'card_clicked' | 'card_timed_out' | 'link_clicked';
  source?: string;
}): void {
  try {
    if (!serverConfig?.widgetId) return;
    const authInfo = getLiveChatAuthInfo({
      locationId: serverConfig.locationId,
      widgetId: serverConfig.widgetId,
      defaultWidget: serverConfig.defaultWidget,
    });
    const body: Record<string, string> = {
      event,
      widgetId: serverConfig.widgetId,
      ...(source ? { source } : {}),
      ...(authInfo.conversationId ? { conversationId: authInfo.conversationId } : {}),
      ...(authInfo.visitorId ? { visitorId: authInfo.visitorId } : {}),
      ...(authInfo.contactId ? { contactId: authInfo.contactId } : {}),
    };
    fetch(serverConfig.marketplaceURL + '/chat-widget/public/interactive-event', {
      method: 'POST',
      keepalive: true,
      body: JSON.stringify(body),
      headers: getOutgoingMessageHeaders({
        locationId: serverConfig.locationId,
        widgetId: serverConfig.widgetId,
        defaultWidget: serverConfig.defaultWidget,
        developerVersion: serverConfig.developerVersion,
      }),
    }).catch(error => {
      // Fire-and-forget, but not invisible: a 4xx/rate-limit from the endpoint should be traceable.
      console.debug('interactive-event post failed', error);
    });
  } catch (error) {
    console.debug('interactive-event post threw', error);
  }
};

export const postFeedback = async function ({
  serverConfig,
  feedback,
  isDummy = DEFAULT_DUMMY_REQUEST,
}: {
  serverConfig: ServerConfig;
  feedback: string;
  isDummy?: boolean;
}): Promise<FeedbackResponse> {
  if (isDummy) {
    return new Promise(resolve => {
      setTimeout(() => {
        let response = {
          success: 'true',

          traceId: 'da4c5312-3139-970b-8201-8e5dc0b4918e',
        };

        resolve(response);
      }, 2000);
    });
  }
  const liveChatAuthData = getLiveChatAuthInfo({
    locationId: serverConfig.locationId,
    widgetId: serverConfig.widgetId,
    defaultWidget: serverConfig.defaultWidget,
  });

  // Backend rejects empty conversationId / visitorId / locationId. Refuse to submit
  // (rather than ship a malformed body that JSON.stringify will drop fields from).
  if (
    !serverConfig.locationId ||
    !serverConfig.widgetId ||
    !liveChatAuthData.conversationId ||
    !liveChatAuthData.visitorId
  ) {
    throw new Error(
      `postFeedback: missing required field — locationId/widgetId/conversationId/visitorId must be set`
    );
  }

  const body: Record<string, string> = {
    locationId: serverConfig.locationId,
    widgetId: serverConfig.widgetId,
    conversationId: liveChatAuthData.conversationId,
    visitorId: liveChatAuthData.visitorId,
    feedback,
    ...(liveChatAuthData.contactId ? { contactId: liveChatAuthData.contactId } : {}),
  };

  const responseData = await fetch(serverConfig.marketplaceURL + '/chat-widget/public/feedback', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: getOutgoingMessageHeaders({
      locationId: serverConfig.locationId,
      widgetId: serverConfig.widgetId,
      defaultWidget: serverConfig.defaultWidget,
      developerVersion: serverConfig.developerVersion,
    }),
  });

  if (!responseData.ok) {
    throw new Error(`postFeedback: HTTP ${responseData.status} ${responseData.statusText}`);
  }
  return (await responseData.json()) as FeedbackResponse;
};

// Essential error handling for socket.io
export const createWebCall = async (
  agentId: string,
  marketplaceURL: string,
  contactId?: string,
  callId?: string,
  widgetId?: string,
  eventData?: any,
  locationId?: string,
  captchaToken?: string,
): Promise<any> => {
  try {
    // Use the agentId in the URL path as shown in the working curl
    const apiUrl = `${marketplaceURL}/chat-widget/public/start-voice-ai-call/${agentId}`;

    const headers: any = {
      'Content-Type': 'application/json',
    };
    let body;
    if (contactId && callId) {
      body = { contactId, callId };
    } else if (contactId) {
      body = { contactId };
    } else {
      body = undefined;
    }

    if (widgetId) {
      body = { ...body, widgetId };
    }
    if (eventData) {
      body = { ...body, ...eventData };
    }
    if (locationId) {
      body = { ...body, locationId };
    }
    if (captchaToken) {
      body = { ...body, captchaToken };
    }
    body = { ...body, useGatewayTransport: true };

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const responseText = await response.text();
    if (!response.ok) {
      let errorData;
      try {
        errorData = JSON.parse(responseText);
      } catch (e) {
        errorData = {};
      }

      return {
        status: response.status,
        statusText: response.statusText,
        error: true,
        errorResponse: responseText,
        errorData: errorData,
        isConcurrentCallLimit:
          errorData.code === 9015 && errorData.error.includes('concurrent calls allowed per location'),
      };
    }

    const result = JSON.parse(responseText);
    return {
      accessToken: result.accessToken,
      callId: result.callId,
      contactId: result.contactId,
      wsUrl: result.wsUrl,
      provider: result.provider,
      sessionToken: result.sessionToken,
      agentProviderCallId: result.agentProviderCallId,
      transport: result.transport,
      iceServers: result.iceServers,
      expiresAt: result.expiresAt,
      status: response.status,
      error: false,
    };
  } catch (error) {
    return {
      status: 500,
      statusText: 'Network Error',
      error: true,
      errorMessage: error.message,
    };
  }
};
